import { getApiPrefix } from '../utils/apiHelper';
import { useState, useEffect, useCallback } from "react";
import { api } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { handleApiError, showSuccess } from "../utils/errorHandler";
import { PageLoader } from "../components/ui/page-loader";
import { Plus, Trash2, CreditCard, Loader2, Pencil } from "lucide-react";
import DataTable from "../components/ui/data-table";
import { useTranslation } from "../contexts/I18nContext";

export default function PaymentMethodsPage() {
  const { t } = useTranslation();
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ codice: "", nome: "", descrizione: "" });
  const [saving, setSaving] = useState(false);

  const fetchMethods = useCallback(async () => {
    try {
      const res = await api.get(`${getApiPrefix()}/payment-methods`);
      setMethods(res.data);
    } catch (err) {
      handleApiError(err, t("errors.loadPaymentMethods"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMethods();
  }, [fetchMethods]);

  const handleSubmit = async () => {
    if (!form.nome) {
      handleApiError({ message: t("errors.requiredName") });
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`${getApiPrefix()}/payment-methods/${editingId}`, form);
        showSuccess(t("success.methodUpdated"));
      } else {
        await api.post(`${getApiPrefix()}/payment-methods`, form);
        showSuccess(t("success.methodCreated"));
      }
      setDialogOpen(false);
      setEditingId(null);
      setForm({ codice: "", nome: "", descrizione: "" });
      fetchMethods();
    } catch (err) {
      handleApiError(err, t("errors.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("confirm.deletePaymentMethod"))) return;
    try {
      await api.delete(`${getApiPrefix()}/payment-methods/${id}`);
      showSuccess(t("success.methodDeleted"));
      fetchMethods();
    } catch (err) {
      handleApiError(err, t("errors.deleteFailed"));
    }
  };

  const handleBulkDelete = async (ids) => {
    let deleted = 0;
    for (const id of ids) {
      try {
        await api.delete(`${getApiPrefix()}/payment-methods/${id}`);
        deleted++;
      } catch (err) { }
    }
    showSuccess(`${deleted} modalità eliminate`);
    fetchMethods();
  };

  const openEdit = (method) => {
    setEditingId(method.id);
    setForm({ codice: method.codice || "", nome: method.nome, descrizione: method.descrizione || "" });
    setDialogOpen(true);
  };

  const columns = [
    { key: 'codice', label: t('common.code'), width: '120px' },
    { key: 'nome', label: t('common.name') },
    { key: 'descrizione', label: t('common.description') },
    { 
      key: 'actions', 
      label: t('common.actions'), 
      align: 'right',
      width: '100px',
      sortable: false, 
      filterable: false,
      render: (_, row) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="sm" onClick={() => openEdit(row)} title={t("common.edit")}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => handleDelete(row.id)} title={t("common.delete")}>
            <Trash2 className="h-4 w-4 text-red-500" />
          </Button>
        </div>
      )
    }
  ];

  if (loading) {
    return <PageLoader variant="section" text={t("common.loading")} />;
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="payment-methods-page">
      <div className="flex items-center gap-3">
        <CreditCard className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-3xl font-bold">{t('invoices.paymentMethod')}</h1>
          <p className="text-muted-foreground">Gestione metodi di pagamento</p>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={methods}
        onDelete={handleBulkDelete}
        idField="id"
        emptyMessage="Nessuna modalità di pagamento trovata"
        toolbar={
          <Button onClick={() => { setEditingId(null); setForm({ codice: "", nome: "", descrizione: "" }); setDialogOpen(true); }}>
            <Plus className="h-4 w-4 mr-2" /> Nuova Modalità
          </Button>
        }
      />

      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) { setEditingId(null); setForm({ codice: "", nome: "", descrizione: "" }); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? t("paymentMethods.editMethod") : t("paymentMethods.newMethod")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("common.code")}</Label>
                <Input value={form.codice} onChange={(e) => setForm({ ...form, codice: e.target.value })} placeholder="Es: BON" />
              </div>
              <div>
                <Label>{t('common.name')} *</Label>
                <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Es: Bonifico" />
              </div>
            </div>
            <div>
              <Label>{t("common.description")}</Label>
              <Textarea value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} placeholder={t("common.optional")} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={handleSubmit} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingId ? t("common.save") : t("common.create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
